import { useState } from 'react';
import { DayPicker, type DateRange } from 'react-day-picker';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import toast from 'react-hot-toast';
import 'react-day-picker/dist/style.css';

import { api } from '../lib/api';

import { UpgradePrompt } from './UpgradePrompt';

interface DataExportProps {
  siteId: string;
  hasPaidSites: boolean;
  className?: string;
}

export function DataExport({ siteId, hasPaidSites, className = "" }: DataExportProps) {
  const [dateRange, setDateRange] = useState<DateRange>({
    from: subDays(new Date(), 30), // Default to last 30 days
    to: new Date(),
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (!dateRange.from || !dateRange.to) {
      toast.error('Please select a date range');
      return;
    }

    if (!siteId) {
      toast.error('No site selected for export');
      return;
    }

    setIsExporting(true);
    
    try {
      const response = await api.exportCsv({
        siteId,
        from: startOfDay(dateRange.from).toISOString(),
        to: endOfDay(dateRange.to).toISOString(),
      });

      // Create a temporary link and trigger download using the blob
      const url = window.URL.createObjectURL(response.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Export downloaded successfully!');
    } catch (error: any) {
      console.error('Export failed:', error);
      toast.error(error.message || 'Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const formatDateRange = () => {
    if (!dateRange.from) return 'Select date range';
    if (!dateRange.to) return format(dateRange.from, 'MMM dd, yyyy');
    if (dateRange.from.getTime() === dateRange.to.getTime()) {
      return format(dateRange.from, 'MMM dd, yyyy');
    }
    return `${format(dateRange.from, 'MMM dd, yyyy')} - ${format(dateRange.to, 'MMM dd, yyyy')}`;
  };

  const isValidDateRange = dateRange.from && dateRange.to && dateRange.from <= dateRange.to;

  if (!hasPaidSites) {
    return (
      <UpgradePrompt
        title="Data Export"
        description="Export your feedback data as CSV files for analysis. This feature is available with Pro plans."
        className={className}
      />
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Export Data</h3>
          <p className="text-sm text-gray-600">
            Download your feedback data as CSV for analysis
          </p>
        </div>
        <div className="text-2xl">📊</div>
      </div>

      <div className="space-y-4">
        {/* Date Range Selector */}
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date Range
          </label>
          <button
            type="button"
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="w-full text-left px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <div className="flex items-center justify-between">
              <span className={isValidDateRange ? 'text-gray-900' : 'text-gray-500'}>
                {formatDateRange()}
              </span>
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </button>

          {/* Date Picker Dropdown */}
          {showDatePicker && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 p-4">
              <DayPicker
                mode="range"
                selected={dateRange}
                onSelect={(range) => {
                  setDateRange(range || { from: undefined, to: undefined });
                  if (range?.from && range?.to) {
                    setShowDatePicker(false);
                  }
                }}
                disabled={{ after: new Date() }}
                numberOfMonths={2}
                classNames={{
                  day_selected: 'bg-blue-600 text-white',
                  day_today: 'bg-blue-100 text-blue-900',
                  day_range_middle: 'bg-blue-100',
                }}
              />
              <div className="flex justify-between mt-4 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setDateRange({
                      from: subDays(new Date(), 7),
                      to: new Date(),
                    });
                    setShowDatePicker(false);
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Last 7 days
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDateRange({
                      from: subDays(new Date(), 30),
                      to: new Date(),
                    });
                    setShowDatePicker(false);
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Last 30 days
                </button>
                <button
                  type="button"
                  onClick={() => setShowDatePicker(false)}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Export Button */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {isValidDateRange && (
              <span>
                Export data from {format(dateRange.from!, 'MMM dd, yyyy')} to {format(dateRange.to!, 'MMM dd, yyyy')}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={handleExport}
            disabled={!isValidDateRange || isExporting}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Exporting...
              </>
            ) : (
              <>
                <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export CSV
              </>
            )}
          </button>
        </div>

        {/* Info Note */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700 font-medium mb-2">
                CSV Export Data Fields:
              </p>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• <strong>Type:</strong> &apos;vote&apos; or &apos;comment&apos; - indicates the feedback type</li>
                <li>• <strong>Site ID:</strong> Your site identifier</li>
                <li>• <strong>Page:</strong> URL or page path where feedback was given</li>
                <li>• <strong>Vote:</strong> &apos;up&apos; or &apos;down&apos; - the user&apos;s feedback choice</li>
                <li>• <strong>Comment:</strong> User&apos;s text comment (empty for votes only)</li>
                <li>• <strong>Location:</strong> Country, region, and city (anonymized)</li>
                <li>• <strong>Timestamp:</strong> ISO date/time when feedback was submitted</li>
                <li>• <strong>User Agent:</strong> Browser/device information</li>
              </ul>
              <p className="text-sm text-blue-700 mt-2">
                <strong>Limits:</strong> Maximum 100,000 records or 365 days per export. Files expire after 24 hours.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}